import React, { useState, useEffect } from 'react';

// --- 1. Types & Interfaces ---

interface FormData {
    name: string;
    email: string;
    phone: string;
    message: string;
}

interface ToastProps {
    message: string;
    isVisible: boolean;
    onClose: () => void;
}

interface BentoCardProps {
    title: string;
    subtitle: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    className?: string;
}

interface ModernInputProps {
    label: string;
    type?: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    required?: boolean;
    rows?: number;
}

// --- 2. Global Styles & Animations ---
const GlobalStyles: React.FC = () => (
    <style>{`
    html { scroll-behavior: smooth; }
    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #f8fafc; }
    ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

    @keyframes drift {
      0% { transform: translate(0px, 0px) scale(1); }
      33% { transform: translate(30px, -50px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
      100% { transform: translate(0px, 0px) scale(1); }
    }
    .animate-drift { animation: drift 20s ease-in-out infinite; }

    .grid-reveal { opacity: 0; transform: translateY(20px); transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1); }
    .grid-reveal.visible { opacity: 1; transform: translateY(0); }

    .glass-panel {
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.5);
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.05);
    }
    .glass-card {
      background: rgba(255, 255, 255, 0.4);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.3);
      transition: all 0.3s ease;
    }
    .glass-card:hover {
      background: rgba(255, 255, 255, 0.6);
      transform: translateY(-2px);
      box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.1);
    }

    .input-group input:focus ~ label,
    .input-group input:not(:placeholder-shown) ~ label {
      transform: translateY(-24px) scale(0.85);
      color: #6366f1;
    }

    @keyframes slideInRight {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    .toast-enter { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
  `}</style>
);

// --- 3. Modern Icons (PascalCase keys to satisfy react/jsx-pascal-case) ---
const Icons = {
    Phone: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>,
    Mail: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>,
    Map: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>,
    Send: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="m22 2-7 20-4-9-9-4Z"></path><path d="M22 2 11 13"></path></svg>,
    Sparkles: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path></svg>,
    ArrowRight: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>,
    Check: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    X: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>,
    Bot: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 8V4H8"></path><rect width="16" height="12" x="4" y="8" rx="2"></rect><path d="M2 14h2"></path><path d="M20 14h2"></path><path d="M15 13v2"></path><path d="M9 13v2"></path></svg>,
    Globe: () => <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path><path d="M2 12h20"></path></svg>,
};

// --- 4. UI Components ---

const Toast: React.FC<ToastProps> = ({ message, isVisible, onClose }) => {
    if (!isVisible) return null;
    return (
        <div className="fixed bottom-6 right-6 z-50 toast-enter">
            <div className="glass-panel bg-white/90 rounded-2xl p-4 shadow-2xl flex items-center gap-4 min-w-[320px] border-l-4 border-green-500">
                <div className="bg-green-100 p-2 rounded-full">
                    <Icons.Check />
                </div>
                <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Message Sent</h4>
                    <p className="text-sm text-gray-600">{message}</p>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <Icons.X />
                </button>
            </div>
        </div>
    );
};

const BentoCard: React.FC<BentoCardProps> = ({ title, subtitle, icon: Icon, className = "" }) => (
    <div className={`glass-card rounded-2xl p-6 relative overflow-hidden group ${className}`}>
        <div className="relative z-10">
            <div className="w-12 h-12 bg-white/50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <div className="text-indigo-600"><Icon /></div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{subtitle}</p>
        </div>
        <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-indigo-100 to-red-100 rounded-full blur-2xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
    </div>
);

const AIAssistant: React.FC = () => {
    const [activeNode, setActiveNode] = useState<number>(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveNode(prev => (prev + 1) % 4);
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="relative h-full min-h-[300px] bg-gradient-to-br from-indigo-900 via-red-900 to-slate-900 rounded-3xl overflow-hidden p-8 flex items-center justify-center">
            <div className="absolute inset-0">
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-red-500/30 rounded-full blur-3xl animate-drift"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-drift" style={{ animationDelay: '-10s' }}></div>
            </div>

            <div className="relative z-10 text-center">
                <div className="w-20 h-20 mx-auto mb-6 relative">
                    <div className="absolute inset-0 bg-indigo-500/20 rounded-full animate-ping"></div>
                    <div className="relative w-full h-full bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center">
                        <Icons.Bot />
                    </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">AI-Powered Support</h3>
                <p className="text-indigo-200 text-sm max-w-xs mx-auto">Our intelligent assistant routes your inquiry to the right expert instantly.</p>
            </div>

            {[0, 1, 2, 3].map((i) => (
                <div
                    key={i}
                    className={`absolute w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${i === activeNode
                        ? 'bg-white/20 border-2 border-white/40 scale-110 z-20'
                        : 'bg-white/5 border border-white/10 z-10'
                        }`}
                    style={{
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${i * 90}deg) translateX(140px) rotate(-${i * 90}deg)`
                    }}
                >
                    <div className={`w-2 h-2 rounded-full ${i === activeNode ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
                </div>
            ))}

            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
                <circle cx="50%" cy="50%" r="140" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4 4" />
            </svg>
        </div>
    );
};

const ModernInput: React.FC<ModernInputProps> = ({ label, type = "text", name, value, onChange, required = false, rows }) => {
    const InputComponent = rows ? 'textarea' : 'input';
    return (
        <div className="input-group relative">
            <InputComponent
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                rows={rows}
                placeholder=" "
                className="w-full px-4 py-4 bg-white/50 border border-gray-200 rounded-xl text-gray-900 placeholder-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all peer"
            />
            <label className="absolute left-4 top-4 text-gray-500 text-sm transition-all duration-300 pointer-events-none origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-85 peer-focus:-translate-y-6">
                {label}
            </label>
        </div>
    );
};

// --- 5. Main Component ---
const Contact: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                    }
                });
            },
            { threshold: 0.1 }
        );

        document.querySelectorAll('.grid-reveal').forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);

        setTimeout(() => {
            console.log('Form submitted:', formData);
            setIsSubmitting(false);
            setToast({ show: true, message: "Our team has received your message and will respond within 2 hours." });
            setFormData({ name: '', email: '', phone: '', message: '' });
            setTimeout(() => setToast({ show: false, message: '' }), 5000);
        }, 1500);
    };

    return (
        <>
            <GlobalStyles />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/50 to-red-50/50 relative overflow-hidden">

                {/* Ambient Background Effects */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-200/40 rounded-full blur-3xl animate-drift"></div>
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-200/40 rounded-full blur-3xl animate-drift" style={{ animationDelay: '-5s' }}></div>
                </div>

                {/* Header Section */}
                <div className="relative z-10 pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                    <div className="text-center max-w-3xl mx-auto grid-reveal">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full border border-white/60 mb-6 shadow-sm">
                            <Icons.Sparkles />
                            <span className="text-sm font-medium text-indigo-900">AI-Powered Customer Experience</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-900 via-red-900 to-indigo-900 mb-6 tracking-tight">
                            Let's Create<br />Something Amazing
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            Connect with our team of experts. Whether you have a question, feedback, or just want to say hello, we're here for you.
                        </p>
                    </div>
                </div>

                {/* Bento Grid Layout */}
                <div className="relative z-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 auto-rows-[minmax(180px,auto)]">

                        {/* AI Assistant Feature (Large Card) */}
                        <div className="lg:col-span-8 grid-reveal">
                            <AIAssistant />
                        </div>

                        {/* Contact Info Cards */}
                        <div className="lg:col-span-4 flex flex-col gap-6">
                            <BentoCard
                                title="Phone Support"
                                subtitle="+233-242-876-315"
                                icon={Icons.Phone}
                                className="grid-reveal flex-1"
                            />
                            <BentoCard
                                title="Email Us"
                                subtitle="hakoomson@guardrisk.org"
                                icon={Icons.Mail}
                                className="grid-reveal flex-1"
                            />
                            <BentoCard
                                title="Visit Us"
                                subtitle="Nmai-Dzorm, Adjiringano Street, Accra Opposite Ability Square. GPS: GD-166-726"
                                icon={Icons.Map}
                                className="grid-reveal flex-1"
                            />
                        </div>

                        {/* Contact Form (Wide Card) */}
                        <div className="lg:col-span-12 grid-reveal">
                            <div className="glass-panel bg-white/80 rounded-3xl p-8 md:p-12 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-indigo-100 to-red-100 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2"></div>

                                <div className="relative z-10 max-w-4xl mx-auto">
                                    <div className="text-center mb-10">
                                        <h2 className="text-3xl font-bold text-gray-900 mb-3">Send us a message</h2>
                                        <p className="text-gray-600">We typically respond within 24 hours.</p>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <ModernInput
                                                label="Full Name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <ModernInput
                                                label="Email Address"
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <ModernInput
                                            label="Phone Number"
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                        />

                                        <ModernInput
                                            label="How can we help?"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            rows={4}
                                        />

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="group relative w-full flex items-center justify-center gap-3 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-70 disabled:cursor-not-allowed overflow-hidden"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                                            {isSubmitting ? (
                                                <>
                                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    Send Message
                                                    <Icons.ArrowRight />
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* Global Presence Map Card */}
                        <div className="lg:col-span-12 grid-reveal">
                            <div className="glass-panel bg-white/60 rounded-3xl p-8">
                                <div className="flex items-center justify-between mb-8">
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">Global Presence</h2>
                                        <p className="text-gray-600 mt-1">Serving customers in Ghana and Beyond</p>
                                    </div>
                                    <button className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg hover:bg-gray-50 transition-colors border border-gray-200">
                                        <Icons.Globe />
                                        <span className="text-sm font-medium">View Location</span>
                                    </button>
                                </div>

                                {/* Simplified Map Placeholder */}
                                <div className="w-full h-[300px] bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl relative overflow-hidden group">
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-center">
                                            <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                                <Icons.Map />
                                            </div>
                                            <p className="text-gray-500 font-medium">Interactive Map Loading...</p>
                                        </div>
                                    </div>
                                    {/* Decorative Map Lines */}
                                    <svg className="absolute inset-0 w-full h-full opacity-20">
                                        <path d="M0,150 Q400,50 800,150 T1600,150" fill="none" stroke="#6366f1" strokeWidth="2" strokeDasharray="10 10" />
                                        <path d="M100,300 Q500,200 900,300 T1700,300" fill="none" stroke="#a855f7" strokeWidth="2" strokeDasharray="10 10" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

            </div>

            <Toast
                isVisible={toast.show}
                message={toast.message}
                onClose={() => setToast({ ...toast, show: false })}
            />
        </>
    );
};

export default Contact;